'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';

const steps = [
  {
    number: '01',
    title: 'Submit Your Application',
    duration: '5–10 minutes',
    description: 'Complete our secure online application form. You will provide basic personal information, employment details, income figures, and the loan amount you are seeking. No hard credit pull occurs at this stage.',
    requirements: ['Government-issued photo ID', 'Social Security Number (last 4 digits)', 'Employment status and employer name', 'Estimated monthly income', 'Desired loan amount and purpose'],
    tip: 'Be as accurate as possible. Inaccurate information can delay your application or result in a denial.',
  },
  {
    number: '02',
    title: 'Verification & Document Review',
    duration: '1–2 business days',
    description: 'Our team reviews your submitted information and may request supporting documentation to verify your identity, income, and employment. This step ensures your application is complete before advancing to underwriting.',
    requirements: ['Recent pay stubs (last 30 days)', 'Bank statements (last 2–3 months)', 'Tax returns or W-2s (last 2 years)', 'Proof of address (utility bill or lease)', 'Business financials (for business loans)'],
    tip: 'Upload documents promptly to avoid delays. All documents are encrypted and stored securely.',
  },
  {
    number: '03',
    title: 'Underwriting Assessment',
    duration: '1–3 business days',
    description: 'A licensed underwriter evaluates your complete financial profile, including credit history, debt-to-income ratio, employment stability, and the purpose of the loan. This assessment determines your eligibility and the terms you qualify for.',
    requirements: ['Credit report review (soft or hard pull depending on program)', 'Debt-to-income ratio calculation', 'Employment and income verification', 'Collateral assessment (for secured loans)', 'Program-specific eligibility criteria'],
    tip: 'A higher credit score and lower debt-to-income ratio generally result in better rates and terms.',
  },
  {
    number: '04',
    title: 'Approval Decision',
    duration: 'Same day to 2 business days',
    description: 'You will receive a formal approval decision along with your loan offer, including the approved amount, interest rate, repayment term, and any conditions that must be met before funding. You are under no obligation to accept.',
    requirements: ['Review and sign loan agreement', 'Acknowledge all terms and disclosures', 'Provide any outstanding conditions', 'Confirm funding account details', 'Complete any required identity verification'],
    tip: 'Read your loan agreement carefully. Contact your advisor with any questions before signing.',
  },
  {
    number: '05',
    title: 'Funding',
    duration: '1–5 business days after signing',
    description: 'Once all conditions are satisfied and your loan agreement is executed, funds are disbursed directly to your designated bank account via ACH transfer. Business loans may be funded via wire transfer.',
    requirements: ['Executed loan agreement on file', 'All conditions cleared by underwriting', 'Valid bank account for ACH deposit', 'Final identity confirmation', 'Funding authorization from compliance'],
    tip: 'ACH transfers typically arrive within 1–3 business days. Wire transfers may arrive same day.',
  },
];

const faqs = [
  { q: 'How long does the entire process take?', a: 'From application submission to funding, the process typically takes 3–10 business days depending on the loan type, the completeness of your documentation, and program-specific requirements.' },
  { q: 'Will applying affect my credit score?', a: 'The initial application uses a soft credit inquiry, which does not affect your credit score. A hard inquiry may be required during underwriting for certain programs, and you will be notified before it occurs.' },
  { q: 'Can I apply if I have less-than-perfect credit?', a: 'Yes. We work with a range of programs designed for various credit profiles. While a higher credit score improves your options, we evaluate your full financial picture — not just your score.' },
  { q: 'What happens if I am not approved?', a: 'If your application is not approved, you will receive a written explanation. Our advisors can discuss alternative programs or steps you can take to improve your eligibility for a future application.' },
  { q: 'Is there a fee to apply?', a: 'There is no fee to submit an application or receive a loan offer. Any applicable origination fees or closing costs will be clearly disclosed in your loan agreement before you sign.' },
  { q: 'Can I cancel after approval?', a: 'Yes. You are not obligated to accept a loan offer. If you have already signed but wish to cancel, contact us immediately. Cancellation policies vary by program and state.' },
];

export default function HowItWorksPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  return (
    <main className="min-h-screen bg-cloud">
      <Header />
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 lg:px-10 bg-white border-b border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-light text-sky-dark text-xs font-semibold font-body mb-6 tracking-wide uppercase">
            The Lending Process
          </div>
          <h1 className="font-heading text-display font-bold text-slate-dark leading-tight mb-6">
            How Lenders Home LLC Works
          </h1>
          <p className="text-lg text-slate-fog font-body leading-relaxed max-w-3xl">
            We have designed a streamlined, transparent lending process that takes you from application to funding with clarity at every step. Here is exactly what to expect.
          </p>
          <div className="flex flex-wrap gap-6 mt-8">
            {[
              { label: 'Average Time to Decision', value: '48 hours' },
              { label: 'Average Time to Funding', value: '3–5 days' },
              { label: 'Application Completion', value: '10 minutes' },
            ]?.map((stat) => (
              <div key={stat?.label} className="bg-sky-light rounded-2xl px-6 py-4 border border-sky/20">
                <div className="font-heading text-xl font-bold text-sky">{stat?.value}</div>
                <div className="text-xs text-slate-fog font-body mt-0.5">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Steps */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex gap-2 mb-10 overflow-x-auto pb-2">
            {steps?.map((step, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold font-body transition-all duration-200 ${
                  activeStep === i
                    ? 'bg-sky text-white shadow-sky/30 shadow-md'
                    : 'bg-white text-slate-fog border border-slate-mist hover:border-sky/40 hover:text-slate'
                }`}
              >
                <span className="font-mono text-xs">{step?.number}</span>
                <span className="hidden sm:inline">{step?.title}</span>
              </button>
            ))}
          </div>

          <div className="bg-white rounded-3xl shadow-card border border-slate-mist/30 overflow-hidden">
            <div className="bg-sky px-8 py-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-sky-light text-sm mb-1">Step {steps?.[activeStep]?.number}</div>
                  <h2 className="font-heading text-2xl font-bold text-white">{steps?.[activeStep]?.title}</h2>
                </div>
                <div className="bg-white/20 rounded-xl px-4 py-2 text-white text-sm font-body flex-shrink-0">
                  ⏱ {steps?.[activeStep]?.duration}
                </div>
              </div>
            </div>
            <div className="p-8 grid lg:grid-cols-2 gap-8">
              <div>
                <p className="text-slate-fog font-body leading-relaxed mb-6">{steps?.[activeStep]?.description}</p>
                <div className="bg-sky-light rounded-2xl p-4 border border-sky/20">
                  <div className="text-xs font-semibold text-sky-dark font-body uppercase tracking-wide mb-2">Pro Tip</div>
                  <p className="text-sm text-slate font-body leading-relaxed">{steps?.[activeStep]?.tip}</p>
                </div>
              </div>
              <div>
                <h3 className="font-heading text-sm font-bold text-slate-dark uppercase tracking-wide mb-4">What You Will Need</h3>
                <ul className="space-y-2.5">
                  {steps?.[activeStep]?.requirements?.map((req) => (
                    <li key={req} className="flex items-start gap-3 text-sm text-slate-fog font-body">
                      <span className="text-sky mt-0.5 flex-shrink-0">✓</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Step navigation */}
          <div className="flex justify-between mt-6">
            <button
              onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
              disabled={activeStep === 0}
              className="px-5 py-2.5 rounded-full text-sm font-semibold font-body border border-slate-mist text-slate-fog hover:border-sky/40 hover:text-slate disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              ← Previous Step
            </button>
            <button
              onClick={() => setActiveStep(Math.min(steps?.length - 1, activeStep + 1))}
              disabled={activeStep === steps?.length - 1}
              className="px-5 py-2.5 rounded-full text-sm font-semibold font-body bg-sky text-white hover:bg-sky-dark disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sky/30 shadow-md"
            >
              Next Step →
            </button>
          </div>
        </div>
      </section>
      {/* Requirements Overview */}
      <section className="py-16 px-6 lg:px-10 bg-white border-y border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl font-bold text-slate-dark mb-3">General Eligibility Requirements</h2>
          <p className="text-slate-fog font-body mb-8">Most lending programs through Lenders Home LLC require the following baseline criteria. Specific programs may have additional requirements.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Age', detail: 'Must be 18 years of age or older' },
              { title: 'Residency', detail: 'Must be a U.S. citizen or permanent resident' },
              { title: 'Income', detail: 'Verifiable income from employment, self-employment, or other sources' },
              { title: 'Bank Account', detail: 'Active U.S. checking or savings account for fund disbursement' },
              { title: 'Identity', detail: 'Valid government-issued photo ID (driver\'s license, passport, state ID)' },
              { title: 'Credit History', detail: 'Credit history reviewed; minimum scores vary by program' },
            ]?.map((item) => (
              <div key={item?.title} className="bg-cloud rounded-2xl p-5 border border-slate-mist/30">
                <div className="font-heading text-sm font-bold text-slate-dark mb-1">{item?.title}</div>
                <div className="text-sm text-slate-fog font-body">{item?.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl font-bold text-slate-dark mb-3">Frequently Asked Questions</h2>
          <p className="text-slate-fog font-body mb-8">Common questions about the lending process at Lenders Home LLC.</p>
          <div className="space-y-3">
            {faqs?.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-mist/30 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left"
                >
                  <span className="font-heading text-sm font-semibold text-slate-dark">{faq?.q}</span>
                  <span className={`text-sky flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-sm text-slate-fog font-body leading-relaxed">{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 px-6 lg:px-10 bg-white border-t border-slate-mist/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl font-bold text-slate-dark mb-4">Ready to Begin?</h2>
          <p className="text-slate-fog font-body mb-8">Start your application in under 10 minutes. No hard credit pull at this stage.</p>
          <Link href="/" className="inline-flex items-center gap-2 px-8 py-3 bg-sky text-white rounded-full font-semibold font-body hover:bg-sky-dark transition-colors shadow-sky/30 shadow-md">
            Start My Application →
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
}
